type BaseDataType = {
	id?: string;
};

type RecordDataUseOptions<M = any> = {
	notEmpty?: boolean;
	notNull?: boolean;
	defaultValue?: M | null;
};

/**
 * This helper class used to aid in the deserialization process.
 *
 * ```
 * const rd = new RecordData<SerializedClassDataType>(dataIn: Partial<SerializedClassDataType>);
 * ```
 */
export class RecordData<DataType> {
	#data: DataType & BaseDataType;
	#newRecord: boolean;
	#partialRecord: boolean;
	#fullRecord: boolean;

	/**
	 * Provide dataIn
	 */
	constructor (dataIn: (DataType & BaseDataType) | null) {
		this.#data = dataIn ?? {} as DataType & BaseDataType;

		// its a new record when dataIn is null or an empty object
		this.#newRecord = this.#data === null || Object.keys(this.#data).length === 0;
		// if not a newRecord and an id is present then treat as a full record
		this.#fullRecord = this.#newRecord ? false : (this.#data?.id || '') !== '';
		// if not a newRecord and there is no id, then it's partial
		this.#partialRecord = this.#newRecord ? false : (this.#data?.id || '') === '';
	}

	/** Are we dealing with a new record; This is true when dataIn is null or an empty object */
	get isNew (): boolean { return this.#newRecord }
	get isNewOrPartial (): boolean { return this.#newRecord || this.#partialRecord }
	get isNewOrFull (): boolean { return this.#newRecord || this.#fullRecord }
	get isPartialOrFull (): boolean { return this.#partialRecord || this.#fullRecord }

	get data (): Partial<DataType> { return this.#data }

	public useNew <M> (propName: string, value: M, options: RecordDataUseOptions | null = null): M {
		let test = this.#newRecord;
		test = this.testOptions<M>(test, propName, options);

		return test ? value : this.data[propName] as M;
	}

	public useNewOrDefault <M> (propName: string, value: M, options: RecordDataUseOptions | null = null): M {
		let test = this.#newRecord;
		test ||= typeof this.#data[propName] === 'undefined';
		test = this.testOptions<M>(test, propName, options);

		return test ? value : this.data[propName] as M;
	}

	public useNewOrPartial <M> (propName: string, value: M, options: RecordDataUseOptions | null = null): M {
		let test = this.#newRecord || this.#partialRecord;
		test = this.testOptions<M>(test, propName, options);

		return test ? value : this.data[propName] as M;
	}

	public usePartialOrFull <M = any> (propName: string, value: M, options: RecordDataUseOptions | null = null): M {
		let test = this.#fullRecord || this.#partialRecord;
		// test &&= typeof this.#data[propName] !== 'undefined';
		test = this.testOptions<M>(test, propName, options);

		return test ? value : this.data[propName] as M;
	}

	// eslint-disable-next-line class-methods-use-this
	private testOptions <M> (testIn: boolean, propName: string, options: RecordDataUseOptions<M> | null): boolean {
		// short circuit
		if (options === null) return testIn;

		const value = this.#data === null ? null : this.#data[propName];
		let test = testIn;

		if (options.defaultValue && typeof value === 'undefined') {
			this.#data[propName] = options.defaultValue;
		} else {
			// test options
			if (options.notEmpty) test &&= value !== '';
			if (options.notNull) test &&= value !== null;
		}

		return test;
	}
}

